// src/context/ThemeContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'high-contrast';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  systemPreference: 'light' | 'dark';
  isHighContrast: boolean;
  toggleHighContrast: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>('light');
  const [systemPreference, setSystemPreference] = useState<'light' | 'dark'>('light');

  // Detect system preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    setSystemPreference(mediaQuery.matches ? 'light' : 'dark');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? 'light' : 'dark');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Load saved theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('medical-app-theme') as Theme;
    if (savedTheme && ['light', 'dark', 'high-contrast'].includes(savedTheme)) {
      setThemeState(savedTheme);
      applyTheme(savedTheme);
    } else {
      // Default to system preference if no saved theme
      const defaultTheme = systemPreference;
      setThemeState(defaultTheme);
      applyTheme(defaultTheme);
    }
  }, [systemPreference]);

  const applyTheme = (newTheme: Theme) => {
    // Remove all theme classes
    document.documentElement.classList.remove('light', 'dark', 'high-contrast');
    
    // Apply new theme class
    document.documentElement.classList.add(newTheme);
    
    // Set data attribute for CSS targeting
    document.documentElement.setAttribute('data-theme', newTheme);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      const colors = {
        light: '#ffffff',
        dark: '#1a1a1a',
        'high-contrast': '#ffffff'
      };
      metaThemeColor.setAttribute('content', colors[newTheme]);
    }
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('medical-app-theme', newTheme);
    
    // Announce theme change to screen readers
    const announcement = `Theme changed to ${newTheme.replace('-', ' ')} mode`;
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    announcer.textContent = announcement;
    document.body.appendChild(announcer);
    setTimeout(() => document.body.removeChild(announcer), 1000);
  };

  const isHighContrast = theme === 'high-contrast';

  const toggleHighContrast = () => {
    if (isHighContrast) {
      // Return to previous theme or system preference
      const previousTheme = localStorage.getItem('medical-app-previous-theme') as Theme;
      setTheme(previousTheme && previousTheme !== 'high-contrast' ? previousTheme : systemPreference);
    } else {
      // Save current theme and switch to high contrast
      localStorage.setItem('medical-app-previous-theme', theme);
      setTheme('high-contrast');
    }
  };

  // Listen for system high contrast preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-contrast: high)');
    
    const handleContrastChange = (e: MediaQueryListEvent) => {
      if (e.matches && theme !== 'high-contrast') {
        // System requests high contrast, but don't override user choice
        console.log('System prefers high contrast. You can enable it in theme settings.');
      }
    };

    mediaQuery.addEventListener('change', handleContrastChange);
    return () => mediaQuery.removeEventListener('change', handleContrastChange);
  }, [theme]);

  // Listen for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleMotionChange = (e: MediaQueryListEvent) => {
      document.documentElement.setAttribute('data-reduced-motion', e.matches.toString());
    };

    // Set initial value
    handleMotionChange(mediaQuery as any);
    
    mediaQuery.addEventListener('change', handleMotionChange);
    return () => mediaQuery.removeEventListener('change', handleMotionChange);
  }, []);

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme, 
      systemPreference, 
      isHighContrast, 
      toggleHighContrast 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};